import { useFetch } from "@lib/hooks/useFetch";
import { StoreRespChema } from "@lib/schemas";
import { FetchStatusInterface, ModalMessage, StorePersonnelPayloadInterface } from "@lib/types";
import { Accessor, ParentProps, Setter, createContext, createSignal } from "solid-js";
import { z } from "zod";

export interface ModalContextInterface {
  fillMessages: Accessor<ModalMessage[]>;
  setFillMessages: Setter<ModalMessage[]>;
  errorMessages: Accessor<ModalMessage[]>;
  setErrorMessages: Setter<ModalMessage[]>;

  // modal message to indicate when the script is waiting for the next page to load
  popupMessage: Accessor<string>;
  setStatusHeader: Setter<string>;

  isFillSuccessful: Accessor<boolean>;
  setIsFillSuccessful: Setter<boolean>;

  isApiKeyValid: Accessor<boolean>;
  setIsApiKeyValid: Setter<boolean>;
  apiCheckData: Accessor<unknown>;
  isApiCheckLoading: Accessor<boolean>;
  apiCheckStatus: Accessor<FetchStatusInterface>;
  fetchApiCheck: (_requesterCallback: () => Promise<Response>) => Promise<void>;

  storeInfo: Accessor<z.infer<typeof StoreRespChema> | null>;
  setStoreInfo: Setter<z.infer<typeof StoreRespChema> | null>;
  clientName: Accessor<string | null>;
  setClientName: Setter<string | null>;

  soid: Accessor<number | null>;
  setSoid: Setter<number | null>;

  personnelUpdateInfo: Accessor<StorePersonnelPayloadInterface>;
  setPersonnelUpdateInfo: Setter<StorePersonnelPayloadInterface>;
  resetContext: () => void;
}

export const ModalContext = createContext<ModalContextInterface>();

function getDefaultPersonnelObject() {
  return {
    new_personnel_first_name: "",
    new_personnel_last_name: "",
    existing_personnel_id: null,
    store_id: null,
  };
}

export function ModalContextProvider(props: ParentProps) {
  const [fillMessages, setFillMessages] = createSignal<ModalMessage[]>([
    { text: "Starting autofill", messageType: "general" },
  ]);
  const [errorMessages, setErrorMessages] = createSignal<ModalMessage[]>([]);
  const [popupMessage, setPopupMessage] = createSignal("");

  const [isFillSuccessful, setIsFillSuccessful] = createSignal(false);
  const [isApiKeyValid, setIsApiKeyValid] = createSignal(true);
  const [apiCheckData, isApiCheckLoading, apiCheckStatus, fetchApiCheck, resetFetch] = useFetch();
  const [storeInfo, setStoreInfo] = createSignal<z.infer<typeof StoreRespChema> | null>(null);
  const [clientName, setClientName] = createSignal<string | null>(null);
  const [soid, setSoid] = createSignal<number | null>(null);
  const [personnelUpdateInfo, setPersonnelUpdateInfo] =
    createSignal<StorePersonnelPayloadInterface>(getDefaultPersonnelObject());

  function resetContext() {
    setFillMessages(() => [{ text: "Starting autofill", messageType: "general" }]);
    setErrorMessages(() => []);
    setPopupMessage(() => "");
    setIsFillSuccessful(() => false);
    setIsApiKeyValid(() => true);
    resetFetch();
    setStoreInfo(() => null);
    setClientName(() => null);
    setSoid(() => null);
    setPersonnelUpdateInfo(() => getDefaultPersonnelObject());
  }

  const contextValues = {
    fillMessages,
    setFillMessages,
    errorMessages,
    setErrorMessages,
    popupMessage,
    setStatusHeader: setPopupMessage,
    isFillSuccessful,
    setIsFillSuccessful,
    isApiKeyValid,
    setIsApiKeyValid,
    apiCheckData,
    isApiCheckLoading,
    apiCheckStatus,
    fetchApiCheck,
    storeInfo,
    setStoreInfo,
    clientName,
    setClientName,
    personnelUpdateInfo,
    setPersonnelUpdateInfo,
    resetContext,
    soid,
    setSoid,
  } satisfies ModalContextInterface;

  return <ModalContext.Provider value={contextValues}>{props.children}</ModalContext.Provider>;
}
